import { useEffect, useMemo, useRef, useState } from "react";
import { FileText, Minus, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { fileToDataUrl, updateRequest } from "../../lib/storage";
import { RESTAURANT_BY_ID } from "../../constants";

// Given the items of a single request + the draft state for this form,
// decide what the resulting status should be:
//   all items received in full    → fulfilled
//   some items received (any qty) → partially_fulfilled
//   nothing received              → missed
function computeStatusFor(items, drafts, requestId) {
  const lineDrafts = items.map((it) => {
    const key = `${requestId}::${it.productId}`;
    const d = drafts[key];
    const ordered = Number(it.quantity ?? it.qty ?? 0);
    const received = d ? Number(d.receivedQty) : Number(it.receivedQty ?? 0);
    return { ordered, received };
  });
  const totalOrdered = lineDrafts.reduce((s, x) => s + x.ordered, 0);
  const totalReceived = lineDrafts.reduce((s, x) => s + x.received, 0);
  if (totalReceived <= 0) return "missed";
  if (totalReceived >= totalOrdered) return "fulfilled";
  return "partially_fulfilled";
}

// Build a flat list of { request, line } rows from a set of requests
// so the dialog can render and collect per-line received quantities.
function buildRows(requests) {
  const rows = [];
  requests.forEach((r) => {
    (r.items || []).forEach((line) => {
      rows.push({ request: r, line });
    });
  });
  return rows;
}

export default function DeliveryConfirmDialog({
  open,
  onOpenChange,
  requests,
  supplier,
  productById,
  user,
  onConfirmed,
}) {
  // Draft state keyed by `${requestId}::${productId}`
  const [drafts, setDrafts] = useState({});
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceFile, setInvoiceFile] = useState("");
  const [invoiceFileName, setInvoiceFileName] = useState("");
  const fileRef = useRef(null);

  const rows = useMemo(() => buildRows(requests || []), [requests]);

  useEffect(() => {
    if (!open) return;
    const next = {};
    rows.forEach(({ request, line }) => {
      const key = `${request.id}::${line.productId}`;
      const ordered = Number(line.quantity ?? line.qty ?? 0);
      const prev = Number(line.receivedQty ?? 0);
      // default: assume full delivery unless a prior confirmation set something else
      next[key] = { receivedQty: line.receivedQty != null ? prev : ordered };
    });
    setDrafts(next);
    // Pre-fill invoice meta from the first request that already has it
    const seeded = (requests || []).find((r) => r.invoiceNumber || r.invoiceDate || r.invoiceFile);
    setInvoiceNumber(seeded?.invoiceNumber || "");
    setInvoiceDate(seeded?.invoiceDate || new Date().toISOString().slice(0, 10));
    setInvoiceFile(seeded?.invoiceFile || "");
    setInvoiceFileName(seeded?.invoiceFileName || "");
  }, [open, rows, requests]);

  const setLine = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const handleFile = async (file) => {
    if (!file) return;
    // Accept image or PDF
    const okType = file.type.startsWith("image/") || file.type === "application/pdf";
    if (!okType) return toast.error("Please choose an image or PDF");
    try {
      if (file.type.startsWith("image/")) {
        const data = await fileToDataUrl(file, 1600, 0.82);
        setInvoiceFile(data);
      } else {
        // PDF: read as base64 data URL (no resize)
        const reader = new FileReader();
        reader.onload = () => setInvoiceFile(String(reader.result || ""));
        reader.onerror = () => toast.error("Couldn't read that file");
        reader.readAsDataURL(file);
      }
      setInvoiceFileName(file.name);
    } catch (_e) {
      toast.error("Couldn't read that file");
    }
  };

  const submit = () => {
    if (!invoiceNumber.trim()) return toast.error("Invoice number required");
    if (!invoiceDate) return toast.error("Invoice date required");
    const nowIso = new Date().toISOString();
    // For each distinct request in scope, patch its items + status + invoice meta
    const byRequest = new Map();
    (requests || []).forEach((r) => byRequest.set(r.id, r));
    byRequest.forEach((r) => {
      const patchedItems = (r.items || []).map((line) => {
        const key = `${r.id}::${line.productId}`;
        const d = drafts[key];
        if (!d) return line;
        const ordered = Number(line.quantity ?? line.qty ?? 0);
        const received = Math.max(0, Math.min(Number(d.receivedQty) || 0, ordered));
        return { ...line, receivedQty: received, confirmed: true };
      });
      const status = computeStatusFor(patchedItems, {}, r.id);
      updateRequest(r.id, {
        items: patchedItems,
        status,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        invoiceFile,
        invoiceFileName,
        deliveryConfirmedAt: nowIso,
        deliveryConfirmedBy: user.id,
      });
    });
    toast.success("Delivery confirmed");
    onConfirmed?.();
    onOpenChange(false);
  };

  const supplierName =
    supplier?.name ||
    (requests?.[0] ? `Request ${requests[0].id.slice(-4)}` : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A] flex items-center gap-2">
            <FileText size={22} /> Confirm delivery
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Match what the vendor delivered against the invoice for <span className="font-medium">{supplierName}</span>. Missing units will be moved to the pending direct-purchase list.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2 space-y-4">
          {/* Invoice meta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Invoice #</Label>
              <Input
                data-testid="invoice-number-input"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="e.g., INV-4821"
                className="mt-1 bg-white border-[#E6E0D4]"
              />
            </div>
            <div>
              <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Invoice date</Label>
              <Input
                data-testid="invoice-date-input"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="mt-1 bg-white border-[#E6E0D4]"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Invoice file (PDF or photo)</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
              <Button
                data-testid="invoice-upload-btn"
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="border-[#E6E0D4] bg-white"
              >
                <Upload size={14} className="mr-1.5" />
                {invoiceFile ? "Replace file" : "Upload file"}
              </Button>
              {invoiceFile && (
                <>
                  <span className="text-xs text-[#0B1F3A]/70 truncate max-w-[220px]">{invoiceFileName || "Attached"}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setInvoiceFile(""); setInvoiceFileName(""); }}
                  >
                    <X size={14} className="mr-1" /> Remove
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Line items */}
          <div className="bg-white border border-[#E6E0D4] rounded-xl divide-y divide-[#EEE8DC]">
            {rows.length === 0 && (
              <div className="p-6 text-center text-sm text-[#0B1F3A]/60">No approved lines to confirm.</div>
            )}
            {rows.map(({ request, line }) => {
              const key = `${request.id}::${line.productId}`;
              const ordered = Number(line.quantity ?? line.qty ?? 0);
              const received = Number(drafts[key]?.receivedQty ?? 0);
              const missing = Math.max(0, ordered - received);
              const product = productById ? productById(line.productId) : null;
              return (
                <div
                  key={key}
                  data-testid={`delivery-line-${request.id}-${line.productId}`}
                  className="px-3 py-3 flex items-start gap-3"
                >
                  {product?.image ? (
                    <img
                      src={product.image}
                      alt=""
                      className="h-10 w-10 rounded object-cover shrink-0"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-[#F3EFE7] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0B1F3A] leading-snug">
                      {product?.name || line.productName || "—"}
                    </p>
                    <p className="text-xs text-[#0B1F3A]/55 mt-0.5">
                      Ordered {ordered} {line.unit || product?.unit || ""} · {RESTAURANT_BY_ID[request.restaurant]?.name}
                      {" · "}{request.requestedByName}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-[#F3EFE7] rounded-full p-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setLine(key, { receivedQty: Math.max(0, received - 1) })}
                      className="h-7 w-7 rounded-full bg-white border border-[#E6E0D4] flex items-center justify-center hover:border-[#0B1F3A]/40"
                      aria-label="Decrease"
                    >
                      <Minus size={14} />
                    </button>
                    <Input
                      data-testid={`received-qty-${request.id}-${line.productId}`}
                      value={received}
                      onChange={(e) => {
                        const v = Math.max(0, Math.min(ordered, Number(e.target.value.replace(/\D/g, "")) || 0));
                        setLine(key, { receivedQty: v });
                      }}
                      inputMode="numeric"
                      className="h-7 w-14 text-center border-0 bg-transparent text-sm font-medium px-0"
                    />
                    <button
                      type="button"
                      onClick={() => setLine(key, { receivedQty: Math.min(ordered, received + 1) })}
                      className="h-7 w-7 rounded-full bg-[#0B1F3A] text-white flex items-center justify-center hover:bg-[#11294A]"
                      aria-label="Increase"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="shrink-0 w-16 text-right">
                    <p className={`text-xs ${missing > 0 ? "text-[#C4552B]" : "text-[#2B7A6B]"}`}>
                      {missing > 0 ? `-${missing} missing` : "✓ full"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="delivery-confirm-submit"
            onClick={submit}
            disabled={rows.length === 0}
            className="bg-[#0B1F3A] hover:bg-[#11294A] text-white disabled:opacity-50"
          >
            Confirm delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
